// Website Analytics Tracking Utility
import config from './config';

class AnalyticsTracker {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.eventQueue = [];
    this.batchSize = 10;
    this.flushInterval = 5000; // 5 seconds
    this.isInitialized = false;
    this.pageLoadTime = Date.now();
    
    // Bind methods to ensure 'this' context is preserved
    this.trackHeatmapClick = this.trackHeatmapClick.bind(this);
    this.trackButtonClick = this.trackButtonClick.bind(this);
    this.trackEvent = this.trackEvent.bind(this);
    this.trackScroll = this.trackScroll.bind(this);
    this.trackFormInteraction = this.trackFormInteraction.bind(this);
    this.flush = this.flush.bind(this);
    this.getClickableLabel = this.getClickableLabel.bind(this);
    this.getElementName = this.getElementName.bind(this);
  }

  init() {
    if (this.isInitialized) {
      console.log('📊 [Analytics] Already initialized, skipping...');
      return;
    }
    
    this.isInitialized = true;
    console.log('📊 [Analytics] Initializing tracker...');
    console.log('📊 [Analytics] Session ID:', this.sessionId);
    console.log('📊 [Analytics] API Endpoint:', `${config.api.baseURL}/website-analytics/events/batch`);
    
    this.trackPageView();
    this.setupAutoTracking();
    this.setupRouteTracking();
    this.startBatchProcessor();
    
    // Track page exit
    window.addEventListener('beforeunload', () => {
      this.flush(true); // Synchronous flush on exit
    });
    
    console.log('✅ [Analytics] Tracker initialized successfully');
  }

  async testConnectivity() {
    // Removed - no longer needed, will just queue events
  }

  /**
   * A session ends after 30 minutes of inactivity, as it does in every standard
   * analytics tool.
   *
   * Previously the id lived in sessionStorage with no expiry, so it survived for
   * the entire life of the browser tab. Someone leaving a tab open all afternoon
   * produced one "session" spanning hours, which is what pushed average session
   * duration to ~50 minutes while bounce rate sat at 90% - two numbers that
   * cannot both be true of the same visitors.
   */
  getOrCreateSessionId() {
    const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;
    const now = Date.now();

    let sessionId = sessionStorage.getItem('tak8_session_id');
    const lastActivity = parseInt(sessionStorage.getItem('tak8_last_activity') || '0', 10);
    const expired = lastActivity > 0 && now - lastActivity > INACTIVITY_LIMIT_MS;

    if (!sessionId || expired) {
      sessionId = `session_${now}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('tak8_session_id', sessionId);
      sessionStorage.setItem('tak8_session_start', now.toString());
    }

    sessionStorage.setItem('tak8_last_activity', now.toString());
    return sessionId;
  }

  /** Refreshes the inactivity window, rotating the session id once it lapses. */
  touchSession() {
    this.sessionId = this.getOrCreateSessionId();
    return this.sessionId;
  }

  /**
   * A stable per-browser id so "unique visitors" counts people rather than
   * sessions. userId below is only ever populated for a signed-in user, and
   * nothing on the site sets it - so without this, distinct-visitor counts were
   * really just session counts wearing a different label.
   */
  getVisitorId() {
    try {
      let visitorId = localStorage.getItem('tak8_visitor_id');
      if (!visitorId) {
        visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('tak8_visitor_id', visitorId);
      }
      return visitorId;
    } catch {
      return null; // Private browsing with storage disabled
    }
  }

  getUserId() {
    // Check if user is logged in (from localStorage or context)
    return localStorage.getItem('tak8_user_id') || null;
  }

  getDeviceInfo() {
    const ua = navigator.userAgent;
    let deviceType = 'desktop';
    
    if (/mobile/i.test(ua)) deviceType = 'mobile';
    else if (/tablet|ipad/i.test(ua)) deviceType = 'tablet';
    
    return {
      deviceType,
      browser: this.getBrowserName(ua),
      os: this.getOSName(ua),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`
    };
  }

  getBrowserName(ua) {
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Unknown';
  }

  getOSName(ua) {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'MacOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS')) return 'iOS';
    return 'Unknown';
  }

  trackEvent(eventType, eventData = {}) {
    const deviceInfo = this.getDeviceInfo();
    
    // Build event with only allowed fields (matching DTO)
    const event = {
      eventType,
      // Rotates the id if the visitor has been idle past the session window.
      sessionId: this.touchSession(),
      visitorId: this.getVisitorId(),
      page: window.location.pathname,
      pageTitle: document.title,
      timestamp: new Date().toISOString(),
      deviceType: deviceInfo.deviceType,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      screenResolution: deviceInfo.screenResolution,
      viewport: deviceInfo.viewport,
    };
    
    // Add optional userId if present
    const userId = this.getUserId();
    if (userId) {
      event.userId = userId;
    }
    
    // Merge allowed additional data
    const allowedFields = [
      'referrer', 'utmSource', 'utmMedium', 'utmCampaign', 'loadTime',
      'element', 'elementId', 'elementTag', 'analyticsName', 'href', 'targetBlank', 'section',
      'formName', 'action', 'fieldName',
      'step', 'carId', 'carName', 'carBrand', 'price', 'hasDiscount', 'discountName',
      'pickUpLocation', 'returnLocation', 'pickUpDate', 'returnDate', 'rentalDays',
      'scrollDepth', 'scrollPosition', 'duration',
      'errorType', 'errorMessage', 'bookingId', 'bookingReference', 'totalAmount',
      'amount', 'paymentMethod', 'metadata',
      'x', 'y', 'viewportWidth', 'viewportHeight' // Heatmap fields
    ];
    
    for (const field of allowedFields) {
      if (eventData[field] !== undefined && eventData[field] !== null) {
        event[field] = eventData[field];
      }
    }

    this.eventQueue.push(event);
    console.log(`📊 [Analytics] Event queued: ${eventType}`, { page: event.page, queueSize: this.eventQueue.length });

    // Flush if queue is full
    if (this.eventQueue.length >= this.batchSize) {
      console.log('📊 [Analytics] Queue full, flushing...');
      this.flush();
    }
  }

  /**
   * Record a page view on every in-app navigation.
   *
   * This is a React Router single-page app: moving from the booking form to
   * /thank-you never reloads the document, so trackPageView() fired exactly
   * once - on the visitor's entry page - and every page after it went
   * unrecorded. Two consequences: the whole post-booking journey was invisible,
   * and bounce rate (defined as "one page view or fewer") read ~90% because
   * almost every visit could only ever record a single view.
   */
  setupRouteTracking() {
    const emitIfChanged = () => {
      const path = window.location.pathname;
      if (path === this.lastTrackedPath) return;
      this.lastTrackedPath = path;
      this.pageLoadTime = Date.now();
      this.trackPageView();
    };

    this.lastTrackedPath = window.location.pathname;

    const { pushState, replaceState } = window.history;

    window.history.pushState = function (...args) {
      const result = pushState.apply(this, args);
      window.dispatchEvent(new Event('tak8:locationchange'));
      return result;
    };

    window.history.replaceState = function (...args) {
      const result = replaceState.apply(this, args);
      window.dispatchEvent(new Event('tak8:locationchange'));
      return result;
    };

    window.addEventListener('tak8:locationchange', emitIfChanged);
    window.addEventListener('popstate', emitIfChanged);
  }

  trackPageView() {
    const referrer = document.referrer || 'direct';
    const urlParams = new URLSearchParams(window.location.search);
    
    this.trackEvent('page_view', {
      referrer,
      utmSource: urlParams.get('utm_source'),
      utmMedium: urlParams.get('utm_medium'),
      utmCampaign: urlParams.get('utm_campaign'),
      loadTime: Date.now() - this.pageLoadTime
    });
  }

  trackButtonClick(buttonName, buttonId, additionalData = {}) {
    this.trackEvent('button_click', {
      element: buttonName,
      elementId: buttonId,
      ...additionalData
    });
  }

  trackFormInteraction(formName, action, fieldName = null) {
    this.trackEvent('form_interaction', {
      formName,
      action, // 'focus', 'blur', 'submit', 'error'
      fieldName
    });
  }

  trackBookingStep(step, data = {}) {
    this.trackEvent('booking_step', {
      step, // 'search', 'car_selection', 'extras', 'checkout', 'payment', 'complete'
      ...data
    });
  }

  trackCarView(carId, carName, carBrand) {
    this.trackEvent('car_view', {
      carId,
      carName,
      carBrand
    });
  }

  trackSearch(searchParams) {
    this.trackEvent('search', {
      pickUpLocation: searchParams.pickUpLocation,
      returnLocation: searchParams.returnLocation,
      pickUpDate: searchParams.pickUpDate,
      returnDate: searchParams.returnDate,
      rentalDays: searchParams.rentalDays
    });
  }

  trackError(errorType, errorMessage, context = {}) {
    this.trackEvent('error', {
      errorType,
      errorMessage,
      ...context
    });
  }

  trackHeatmapClick(e) {
    // Track all clicks for heatmap visualization
    const target = e.target;
    
    // Get meaningful element name
    const elementName = this.getElementName(target);
    
    this.trackEvent('heatmap_click', {
      element: elementName,
      elementId: target?.id || '',
      x: e.clientX,
      y: e.clientY,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight
    });
  }

  // Get a human-readable name for the clicked element
  getElementName(element) {
    if (!element) return 'Unknown';
    
    // Priority 1: Check for data-analytics-name attribute (custom tracking)
    if (element.dataset?.analyticsName) {
      return element.dataset.analyticsName;
    }
    
    // Priority 2: Check for aria-label (accessibility label)
    if (element.getAttribute('aria-label')) {
      return element.getAttribute('aria-label');
    }
    
    // Priority 3: Check for title attribute
    if (element.title) {
      return element.title;
    }
    
    // Priority 3.5: Form controls, BEFORE any textContent fallback.
    //
    // A <select> element's textContent is every one of its <option> labels run
    // together, so the pick-up location dropdown reported itself as
    // Concatenated select text can contain every option label.
    // in one unreadable string, identical for every location.
    if (element.tagName === 'SELECT' || element.tagName === 'TEXTAREA') {
      return this.getFieldLabel(element);
    }

    // Priority 4: Button or link text content
    const text = element.textContent?.trim();
    if (text && text.length > 0 && text.length < 50) {
      // Clean up the text
      const cleanText = text.replace(/\s+/g, ' ').substring(0, 40);
      if (cleanText) return cleanText;
    }
    
    // Priority 5: Input placeholder or value
    if (element.tagName === 'INPUT') {
      if (element.placeholder) return `Input: ${element.placeholder}`;
      if (element.name) return `Input: ${element.name}`;
      if (element.type) return `${element.type} input`;
    }
    
    // Priority 6: Image alt text
    if (element.tagName === 'IMG' && element.alt) {
      return `Image: ${element.alt}`;
    }
    
    // Priority 7: Check parent for better context
    const parent = element.closest('button, a, [role="button"], .btn, nav, header, footer, form');
    if (parent && parent !== element) {
      const parentText = parent.textContent?.trim();
      if (parentText && parentText.length > 0 && parentText.length < 50) {
        return parentText.replace(/\s+/g, ' ').substring(0, 40);
      }
      if (parent.getAttribute('aria-label')) {
        return parent.getAttribute('aria-label');
      }
    }
    
    // Priority 8: Element ID or class name
    if (element.id) {
      return this.formatIdOrClass(element.id);
    }
    
    if (element.className && typeof element.className === 'string') {
      const mainClass = element.className.split(' ')[0];
      if (mainClass && !mainClass.startsWith('css-')) {
        return this.formatIdOrClass(mainClass);
      }
    }
    
    // Priority 9: Section context
    const section = element.closest('section, article, aside, main');
    if (section) {
      const sectionId = section.id || section.className?.split(' ')[0];
      if (sectionId) {
        return `${this.formatIdOrClass(sectionId)} area`;
      }
    }
    
    // Fallback: Element tag with context
    const tag = element.tagName?.toLowerCase() || 'element';
    return `${tag} click`;
  }

  /**
   * Which part of the page a click happened in - header, footer, the search
   * form, a named section - so a report can say WHERE a button lives rather
   * than just naming the button. Two "Next" buttons on different sections are
   * otherwise indistinguishable in the click map.
   */
  getSectionName(element) {
    const landmark = element.closest('header, footer, nav, aside, form, section, article, main, [data-analytics-section]');
    if (!landmark) return null;

    const explicit = landmark.getAttribute('data-analytics-section')
      || landmark.getAttribute('aria-label');
    if (explicit) return explicit.trim().substring(0, 40);

    const tag = landmark.tagName.toLowerCase();
    const byTag = {
      header: 'Header',
      footer: 'Footer',
      nav: 'Navigation',
      aside: 'Sidebar',
      main: 'Main content',
    };
    if (byTag[tag]) return byTag[tag];

    if (landmark.id) return this.formatIdOrClass(landmark.id);

    if (landmark.className && typeof landmark.className === 'string') {
      const mainClass = landmark.className.split(' ').find(
        (c) => c && !c.startsWith('css-') && !/^[a-z]+-\d+$/.test(c),
      );
      if (mainClass) return this.formatIdOrClass(mainClass);
    }

    return tag === 'form' ? 'Form' : null;
  }

  /**
   * A readable name for a form field, never its option list.
   * Looks for an aria-label, then a <label for=...>, then a wrapping <label>,
   * then the field's own name/id.
   */
  getFieldLabel(element) {
    const aria = element.getAttribute('aria-label');
    if (aria) return aria.trim();

    if (element.id) {
      const labelFor = document.querySelector(`label[for="${CSS.escape(element.id)}"]`);
      const forText = labelFor?.textContent?.trim();
      if (forText) return forText.replace(/\s+/g, ' ').substring(0, 40);
    }

    const wrapping = element.closest('label');
    if (wrapping) {
      // Strip the control's own text so option labels do not leak back in.
      const clone = wrapping.cloneNode(true);
      clone.querySelectorAll('select, option, input, textarea').forEach((n) => n.remove());
      const labelText = clone.textContent?.trim();
      if (labelText) return labelText.replace(/\s+/g, ' ').substring(0, 40);
    }

    if (element.name) return this.formatIdOrClass(element.name);
    if (element.id) return this.formatIdOrClass(element.id);
    if (element.placeholder) return element.placeholder;

    return element.tagName === 'SELECT' ? 'Dropdown' : 'Field';
  }

  /** The human-readable text of a select's chosen option. */
  getSelectedOptionText(selectEl) {
    const option = selectEl.options?.[selectEl.selectedIndex];
    const text = option?.textContent?.trim();
    if (!text) return null;
    return text.replace(/\s+/g, ' ').substring(0, 40);
  }

  // Convert camelCase or kebab-case to readable text
  formatIdOrClass(str) {
    if (!str) return 'Unknown';
    return str
      .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase to spaces
      .replace(/[-_]/g, ' ') // kebab-case/snake_case to spaces
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Improved label extractor using closest() - recommended by ChatGPT
  getClickableLabel(target) {
    // Find the closest clickable element (button, link, etc.)
    const el = target.closest('button, a, [role="button"], input[type="button"], input[type="submit"], .btn, .BtnFill, .BtnBorderY');
    
    if (!el) {
      return { 
        element: null, 
        elementId: null, 
        elementTag: null, 
        analyticsName: null,
        href: null,
        targetBlank: false
      };
    }
    
    // Priority order for getting the element name
    const analyticsName = el.getAttribute('data-analytics-name');
    const ariaLabel = el.getAttribute('aria-label');
    const title = el.getAttribute('title');
    const id = el.getAttribute('id');
    
    // Get text content but clean it up
    let text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    // Limit text length
    if (text.length > 50) {
      text = text.substring(0, 47) + '...';
    }
    
    // Determine the best element name (priority order)
    const element = analyticsName || ariaLabel || title || text || id || el.tagName.toLowerCase();
    
    return {
      element: element || 'Unknown Click',
      elementId: id || null,
      elementTag: el.tagName.toLowerCase(),
      analyticsName: analyticsName || null,
      // Which part of the page the control lives in, so reports can say
      // "Home > Search form > Find My Ride" instead of just "Find My Ride".
      section: this.getSectionName(el),
      href: el.href || null,
      targetBlank: el.target === '_blank'
    };
  }

  trackScroll(scrollDepth) {
    // Throttled scroll tracking
    if (!this.lastScrollTrack || Date.now() - this.lastScrollTrack > 3000) {
      this.trackEvent('scroll', {
        scrollDepth: Math.round(scrollDepth),
        scrollPosition: window.scrollY
      });
      this.lastScrollTrack = Date.now();
    }
  }

  setupAutoTracking() {
    // Auto-track all button clicks with heatmap data
    document.addEventListener('click', (e) => {
      // Use improved label extraction with closest()
      const clickData = this.getClickableLabel(e.target);
      
      if (clickData.element) {
        this.trackButtonClick(clickData.element, clickData.elementId, {
          href: clickData.href,
          targetBlank: clickData.targetBlank,
          elementTag: clickData.elementTag,
          analyticsName: clickData.analyticsName,
          x: e.clientX,
          y: e.clientY,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight
        });
      }
      
      // Also track all clicks for heatmap (not just buttons)
      this.trackHeatmapClick(e);
    });

    // Track scroll depth
    let maxScroll = 0;
    window.addEventListener('scroll', () => {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        this.trackScroll(scrollPercent);
      }
    });

    // Track form interactions
    document.addEventListener('focus', (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        const form = e.target.closest('form');
        const formName = form?.name || form?.id || 'unknown';
        const fieldName = e.target.name || e.target.id || e.target.placeholder;
        this.trackFormInteraction(formName, 'focus', fieldName);
      }
    }, true);

    /**
     * Record the option a visitor actually chose.
     *
     * Only 'focus' was tracked before, so analytics knew someone opened the
     * pick-up location dropdown but never which branch they picked - every
     * location looked identical. Reported as a button_click so each choice
     * appears as its own row in Button Click Analytics and the click map,
     * e.g. "Pick-up location: East Perth" vs "Pick-up location: Perth Airport".
     */
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (!target || target.tagName !== 'SELECT') return;

      const fieldLabel = this.getFieldLabel(target);
      const chosen = this.getSelectedOptionText(target);
      if (!chosen) return;

      const form = target.closest('form');

      this.trackEvent('button_click', {
        element: `${fieldLabel}: ${chosen}`,
        analyticsName: `${fieldLabel}: ${chosen}`,
        elementTag: 'select',
        elementId: target.id || target.name || undefined,
        fieldName: fieldLabel,
        formName: form?.name || form?.id || undefined,
        action: 'select',
      });
    }, true);

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackEvent('page_hidden');
        this.flush(); // Flush events when page becomes hidden
      } else {
        this.trackEvent('page_visible');
      }
    });
  }

  startBatchProcessor() {
    console.log(`📊 [Analytics] Starting batch processor (interval: ${this.flushInterval}ms)`);
    setInterval(() => {
      if (this.eventQueue.length > 0) {
        console.log(`📊 [Analytics] Batch processor tick - ${this.eventQueue.length} events in queue`);
        this.flush();
      }
    }, this.flushInterval);
  }

  async flush(synchronous = false) {
    if (this.eventQueue.length === 0) {
      return; // Silent return, no logging needed
    }

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    const payload = {
      events: eventsToSend
    };

    // Use the NEW separate website-analytics endpoint
    const endpoint = `${config.api.baseURL}/website-analytics/events/batch`;

    console.log(`📊 [Analytics] Flushing ${eventsToSend.length} events to: ${endpoint}`);
    console.log('📊 [Analytics] Payload:', JSON.stringify(payload, null, 2));

    try {
      if (synchronous) {
        // Use sendBeacon for synchronous sending (on page unload)
        const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
        const success = navigator.sendBeacon(endpoint, blob);
        console.log(`📊 [Analytics] sendBeacon result: ${success}`);
      } else {
        // Use fetch for async sending with explicit CORS settings
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        console.log('📊 [Analytics] Sending fetch request...');
        const response = await fetch(endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        const responseData = await response.json();
        console.log(`📊 [Analytics] Response status: ${response.status}`);
        console.log('📊 [Analytics] Response data:', responseData);
        
        if (!response.ok) {
          console.error('❌ [Analytics] Server error:', response.status, responseData);
          // Re-queue events on server error (silently)
          if (this.eventQueue.length < 50) {
            this.eventQueue.unshift(...eventsToSend);
          }
        } else {
          console.log(`✅ [Analytics] Successfully sent ${eventsToSend.length} events`);
        }
      }
    } catch (error) {
      console.error('❌ [Analytics] Flush error:', error.message);
      // Silently re-queue events on failure (limit to prevent memory issues)
      if (this.eventQueue.length < 50) {
        this.eventQueue.unshift(...eventsToSend);
      }
    }
  }

  // Session tracking
  updateSessionDuration() {
    const sessionStart = parseInt(sessionStorage.getItem('tak8_session_start') || Date.now().toString());
    const duration = Math.floor((Date.now() - sessionStart) / 1000); // in seconds
    return duration;
  }

  endSession() {
    this.trackEvent('session_end', {
      duration: this.updateSessionDuration()
    });
    this.flush(true);
  }
}

// Create singleton instance
const analytics = new AnalyticsTracker();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.tak8Analytics = {
    flush: () => analytics.flush(),
    getQueue: () => analytics.eventQueue,
    getSessionId: () => analytics.sessionId,
    isInitialized: () => analytics.isInitialized,
    trackTestEvent: () => {
      analytics.trackEvent('test_event', { test: true, timestamp: Date.now() });
      console.log('📊 [Analytics] Test event added to queue');
    }
  };
  console.log('📊 [Analytics] Debug functions available at window.tak8Analytics');
}

// Export for use in components
export default analytics;

// Helper functions for easy use
export const trackPageView = () => analytics.trackPageView();
export const trackButtonClick = (name, id, data) => analytics.trackButtonClick(name, id, data);
export const trackBookingStep = (step, data) => analytics.trackBookingStep(step, data);
export const trackCarView = (carId, name, brand) => analytics.trackCarView(carId, name, brand);
export const trackSearch = (params) => analytics.trackSearch(params);
export const trackError = (type, message, context) => analytics.trackError(type, message, context);
export const initAnalytics = () => analytics.init();
